import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useUserStore } from '@stores/userStore';
import { useMasterScriptsQuery } from '@features/scripts/hooks/useScripts';
import {
  equipmentRecordKeys,
  fetchEquipmentRecord
} from '@api/enterprise/equipment/queries/useEquipmentRecord';
import { matchScriptByProcedureName } from '../utils/scriptMatch';
import type {
  EquipmentViewModel,
  WorkOrderDetailViewModel
} from '@api/enterprise/openapi/models';
import type { Script } from '@features/scripts/types';

export type RackCellResolution = {
  equipment: EquipmentViewModel | null;
  resolvedScript: Script | null;
};

const sanitizeEquipmentId = (id: string | null | undefined): string =>
  (id ?? '')
    .replace(/\r\n/g, '  ')
    .replace(/[\r\n]/g, ' ')
    .trim();

export const useRackScriptResolution = (
  details: WorkOrderDetailViewModel[]
) => {
  const storeCompanyName = useUserStore(state => state.employeeCompany) ?? '';

  const occupiedCells = useMemo(
    () =>
      details
        .filter(
          detail =>
            detail.sequenceNumber != null &&
            sanitizeEquipmentId(detail.id) !== ''
        )
        .map(detail => ({
          sequenceNumber: detail.sequenceNumber as number,
          companyName: detail.companyName || storeCompanyName,
          equipmentId: sanitizeEquipmentId(detail.id)
        })),
    [details, storeCompanyName]
  );

  const equipmentQueries = useQueries({
    queries: occupiedCells.map(cell => ({
      queryKey: equipmentRecordKeys.byId(cell.companyName, cell.equipmentId),
      queryFn: () => fetchEquipmentRecord(cell.companyName, cell.equipmentId),
      enabled: !!cell.companyName && !!cell.equipmentId,
      staleTime: 5 * 60 * 1000
    }))
  });

  const { data: scriptsResponse, isLoading: isLoadingScripts } =
    useMasterScriptsQuery();

  const masterScripts = useMemo(
    () => scriptsResponse?.data ?? [],
    [scriptsResponse]
  );

  const isLoadingEquipment = equipmentQueries.some(query => query.isLoading);

  const resolutionBySequence = useMemo(() => {
    const map = new Map<number, RackCellResolution>();
    occupiedCells.forEach((cell, index) => {
      const equipment = equipmentQueries[index]?.data ?? null;
      const resolvedScript = matchScriptByProcedureName(
        equipment?.procedureName,
        masterScripts
      );
      map.set(cell.sequenceNumber, { equipment, resolvedScript });
    });
    return map;
  }, [occupiedCells, equipmentQueries, masterScripts]);

  return {
    resolutionBySequence,
    isLoading: isLoadingScripts || isLoadingEquipment
  };
};
